import { useState } from 'react'
import cx from 'classnames'
import * as Accordion from '@radix-ui/react-accordion'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import {
  Block,
  Document,
  Inline,
  Text,
  helpers,
} from '@contentful/rich-text-types'

import Nl2br from '../../Nl2br'

import { ChevronIcon } from 'icons'
import { useTracking } from '../../../services/context/TrackingContext'

const faqSlugify = (sectionHeading: string, index: number) => {
  return `faq-${sectionHeading.replace(/\s/g, '-').toLowerCase()}-${index}`
}

export interface IFaqContent {
  heading: string
  sectionsCollection: {
    items: {
      heading: string
      questionsCollection: {
        items: {
          question: string
          answer: {
            json: Document
          }
        }[]
      }
    }[]
  }
}

interface IFaqProps {
  content: IFaqContent
}

// Plain text for the JSON-LD answer: inline text runs together, and each block
// (paragraph, list item, heading) goes on its own line. Empty paragraphs, which
// Contentful leaves behind as "\n", are dropped.
const nodeToText = (node: Document | Block | Inline | Text): string => {
  if (helpers.isText(node)) return node.value
  const blocks: Array<Block | Inline | Text> = node.content
  return blocks
    .map((child) => {
      const text = nodeToText(child)
      return helpers.isBlock(child) ? `\n${text}\n` : text
    })
    .join('')
}

const richTextToPlainText = (document: Document) =>
  nodeToText(document)
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')

// The block ends with a "Still have questions? ... Schedule a time to talk"
// entry that is a call to action, not a question. Match it by that opening
// phrase rather than by position, so a real question is never dropped if the
// CTA is moved or removed in Contentful.
const isCallToAction = (question: string) =>
  /^still have questions\b/i.test(question.trim())

// JSON.stringify leaves <, >, & and U+2028/2029 as-is, which could close the
// <script> tag or break parsing. Escape them; they only occur inside strings.
const serializeJsonLd = (data: object) =>
  JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

const faqPageJsonLd = (content: IFaqContent) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (content.sectionsCollection?.items ?? [])
    .flatMap((section) => section.questionsCollection?.items ?? [])
    .filter((item) => !isCallToAction(item.question))
    .map((item) => ({
      '@type': 'Question',
      name: item.question.trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: richTextToPlainText(item.answer.json),
      },
    })),
})

const Faq = ({ content }: IFaqProps) => {
  const sections = content.sectionsCollection?.items ?? []
  const { track } = useTracking()
  // Items opened at least once, keyed by `${sectionIndex}:${question}`.
  const [openedItems, setOpenedItems] = useState<string[]>([])
  const jsonLd = faqPageJsonLd(content)

  return (
    <section id="faq" className="overflow-hidden rounded bg-white lg:flex">
      {jsonLd.mainEntity.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
        />
      )}

      <aside className="hidden w-1/4 space-y-24 border-r border-[#DEE4E4] bg-grey-2 px-32 py-62 lg:block">
        {sections.map((section) => (
          <div key={section.heading}>
            <h3 className="font-semibold tracking-1 text-web-darkest">
              {section.heading}
            </h3>
            <ul className="mt-14 space-y-14">
              {section.questionsCollection?.items.map((item, i) => (
                <li key={item.question}>
                  <a
                    className="block font-semibold tracking-1 text-teal-3"
                    href={`#${faqSlugify(section.heading, i)}`}
                    onClick={() => {
                      track({
                        event: 'FAQ Item Selected',
                        properties: {
                          question_name: item.question,
                        },
                      })
                    }}
                  >
                    {item.question}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </aside>
      <article className="px-20 pt-40 pb-40 lg:w-3/4 lg:px-48 lg:pt-62">
        <h2 className="text-center text-[28px] font-semibold leading-tight text-web-darkest lg:text-left lg:text-[36px]">
          <Nl2br brClassName="lg:hidden" str={content.heading} />
        </h2>
        <ul className="mt-48 space-y-32 lg:mt-32">
          {sections.map((section, si) => (
            <li key={section.heading}>
              <h3 className="uppercase text-tan-3 util-eyebrow">
                {section.heading}
              </h3>

              <Accordion.Root
                type="multiple"
                onValueChange={(event) => {
                  track({
                    event: 'FAQ Item Opened',
                    properties: {
                      question_name: event[0],
                    },
                  })
                  // Always a new array, so every toggle re-renders. Radix 0.1.6
                  // measures the panel height in a layout effect but only
                  // reads it on its next render, which forceMount no longer
                  // triggers; this render (onValueChange runs in an effect,
                  // after the measurement) keeps the animation height fresh.
                  setOpenedItems((prev) =>
                    prev.concat(
                      event
                        .map((value) => `${si}:${value}`)
                        .filter((key) => !prev.includes(key))
                    )
                  )
                }}
              >
                {section.questionsCollection?.items.map((item, i) => (
                  <Accordion.Item
                    key={item.question}
                    value={item.question}
                    id={faqSlugify(section.heading, i)}
                    className={cx('border-b border-green-1 py-10', {
                      'last:border-0': si + 1 === sections.length,
                    })}
                  >
                    <Accordion.Header asChild>
                      <h4>
                        <Accordion.Trigger
                          className={cx(
                            'group flex w-full items-center justify-between py-6 text-left text-18 font-semibold tracking-1 text-web-darkest transition-colors duration-200',
                            'hover:bg-grey-2 hover:text-teal-3',
                            'state-open:text-teal-3'
                          )}
                        >
                          {item.question}
                          <ChevronIcon className="ml-8 stroke-grey-8 transition duration-200 rect-22 group-state-open:rotate-180 group-state-open:stroke-teal-3" />
                        </Accordion.Trigger>
                      </h4>
                    </Accordion.Header>
                    {/*
                      forceMount keeps every answer in the server HTML, so
                      closed panels are no longer removed and need hiding:
                      - This wrapper collapses an item until it is first
                        opened. Before that Radix keeps animations off, so the
                        panel sits at full height (which it measures for the
                        first open animation).
                      - After a toggle, the close animation's fill holds the
                        panel at height 0, as before.
                      - Closed panels are visibility: hidden once the close
                        animation ends (the delay), which keeps their text out
                        of the tab order and the accessibility tree.
                    */}
                    <div
                      className={cx({
                        'h-0 overflow-hidden': !openedItems.includes(
                          `${si}:${item.question}`
                        ),
                      })}
                    >
                      <Accordion.Content
                        forceMount
                        className={cx(
                          'overflow-hidden [--animation-duration:200ms]',
                          'state-open:animate-radix-accordion-open state-closed:animate-radix-accordion-close',
                          'transition-[visibility] duration-0 state-closed:invisible state-closed:delay-[var(--animation-duration)]'
                        )}
                      >
                        <div className="text space-y-24 py-6 text-web-darkest">
                          {documentToReactComponents(item.answer.json)}
                        </div>
                      </Accordion.Content>
                    </div>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </li>
          ))}
        </ul>
      </article>
    </section>
  )
}

export default Faq
